import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { TaskType } from 'shovel-lib/types';

import CheckBox from '@components/common/CheckBox';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import {
  EstimatePopupLabel,
  EstimatePopupPanel,
  estimatePopupAppendTo,
  POP_OUT_MS,
  useFocusFieldOnOpen
} from '@components/task/partials/EstimatePopup';
import { taskActions } from '@state/task';
import colors from '@utils/colors';
import { formatPlanningMinutes, TASK_ESTIMATE_OPTIONS } from '@utils/taskPlanning';
import { MiniText } from '@utils/typography';

import { isRatePreset, parsePagesInput } from './utils';
import {
  ConvertChoice,
  DefaultQuestion,
  DefaultToast,
  DetectedMeta,
  EditedMeta,
  Pill,
  PillGroup,
  QuestionRow,
  TaskLeft,
  TaskList,
  TaskName,
  TaskOpen,
  TaskRightGroup,
  TaskRow
} from './TaskEstimator.styles';
import { CustomDurationPop, CustomPagesPop, EstimatorChip, RatePills, readingChip, regularChip } from './EstimatorControls';
import { EstimatorCategory, EstimatorReadingSource, EstimatorTask } from './types';
import { EstimatorAction, TaskEstimatorModel } from './useTaskEstimator';

type Act = (action: EstimatorAction) => void;

// The popup and the ⋯ menu are each placed for what they show first; placed again as that changes, a longer list
// still flips above the row rather than running off the screen.
type TippyHandle = { popperInstance?: { update: () => void } | null } | null;

/**
 * A task's own estimate opens in the sidebar's estimate popup, anchored to the row's time so it's plain which task it
 * is for, with the task's name on top. It only estimates: clearing the time is the × on the time, and the task's
 * other actions are in its ⋯ menu. Picking closes it, shrinking back into the time, as in the sidebar.
 */
const TaskEstimatePopup = ({
  task,
  model,
  trigger,
  children
}: {
  task: EstimatorTask;
  model: TaskEstimatorModel;
  trigger: (toggle: () => void) => ReactNode;
  children: (act: Act) => ReactNode;
}) => {
  const { state, dispatch } = model;
  const open = state.ui.openTaskId === task.id;
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const shrinkTimer = useRef<number>();
  useFocusFieldOnOpen(open, panelRef);

  useEffect(() => () => window.clearTimeout(shrinkTimer.current), []);

  // Dispatches and plays the shrink back into the time. The timer is cleared on unmount rather than checked against a
  // "mounted" flag: a flag set only on unmount goes stale when the code reloads with the popup open, and the popup then
  // stayed on the page, shrunk to nothing but still in the way of clicks.
  const act: Act = action => {
    dispatch(action);
    setClosing(true);
    window.clearTimeout(shrinkTimer.current);
    shrinkTimer.current = window.setTimeout(() => setClosing(false), POP_OUT_MS);
  };
  const toggle = () => (open ? act({ type: 'CLOSE_TASK_POPUP' }) : dispatch({ type: 'TOGGLE_TASK_POPUP', taskId: task.id }));

  return (
    <TippyTooltip
      target={<ChipSlot>{trigger(toggle)}</ChipSlot>}
      content={
        // Shrunk whenever it isn't open, not only while it shrinks: the tooltip still fades it out after that, and a
        // panel let go of its shrink then sprang back in on the way out (Petr: "a weird animation" on closing it from its
        // time). The sidebar's popup does the same (PileEstimateControl).
        <EstimatePopupPanel ref={panelRef} $closing={!open}>
          <PopupTaskName title={task.title}>{task.title}</PopupTaskName>
          {children(act)}
        </EstimatePopupPanel>
      }
      // Above the time, as in the sidebar, so it doesn't cover the rows under it; below only when there's no room.
      position="top-end"
      interactive
      controlled
      visible={open || closing}
      onClickOutside={() => open && act({ type: 'CLOSE_TASK_POPUP' })}
      className="estimate-popup"
      lightTheme
      noPadding
      arrow={false}
      width="none"
      appendTo={estimatePopupAppendTo}
      zIndex={200}
    />
  );
};

/**
 * The task's name, and the room beside it, open the task in its own dialog over the Estimator, as an Auto-plan row
 * does: its details, notes and title are there. What's saved there comes back into the Estimator (useTaskEstimator).
 */
const TaskOpenArea = ({ task, children }: { task: EstimatorTask; children?: ReactNode }) => {
  const dispatch = useDispatch();
  const openTask = () => dispatch(taskActions.openManageTaskDialog({ taskId: task.id, type: TaskType.SHOVEL }));
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openTask();
  };
  return (
    <TaskOpen role="button" tabIndex={0} aria-label={`Open ${task.title}`} onClick={openTask} onKeyDown={onKeyDown}>
      <TaskName $trashed={task.trashed} title={task.title}>
        {task.title}
      </TaskName>
      {children}
    </TaskOpen>
  );
};

/**
 * The task's other actions, behind the ⋯ the sidebar's courses use: move it to another category, estimate it the
 * other way (pages or time), or trash it. Move and the other way open their choices in the same menu, with Back.
 */
const TaskActionsMenu = ({ task, model }: { task: EstimatorTask; model: TaskEstimatorModel }) => {
  const { state, dispatch } = model;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'menu' | 'move' | 'convert'>('menu');
  const tippy = useRef<TippyHandle>(null);
  const isReading = task.kind === 'reading';

  useEffect(() => {
    if (open) tippy.current?.popperInstance?.update();
  }, [open, view]);

  const close = () => {
    setOpen(false);
    setView('menu');
  };
  const run = (action: EstimatorAction) => {
    dispatch(action);
    close();
  };
  const toggle = () => {
    if (open) return close();
    // One popup at a time: the task's estimate popup, if open, gives way.
    if (state.ui.openTaskId != null) dispatch({ type: 'CLOSE_TASK_POPUP' });
    setOpen(true);
  };

  const menu = (
    <MenuList>
      {!task.trashed && !isReading && state.categories.length > 0 && (
        <MenuItem type="button" onClick={() => setView('move')}>
          <MaterialIcon name="drive_file_move" outline size={16} color="currentColor" />
          Move to category
        </MenuItem>
      )}
      {!task.trashed && (
        <MenuItem type="button" onClick={() => setView('convert')}>
          <MaterialIcon name={isReading ? 'schedule' : 'menu_book'} outline size={16} color="currentColor" />
          {isReading ? 'Estimate by time instead' : 'Estimate by pages instead'}
        </MenuItem>
      )}
      {task.trashed ? (
        <MenuItem type="button" onClick={() => run({ type: 'UNTRASH_TASK', taskId: task.id })}>
          <MaterialIcon name="restore_from_trash" outline size={16} color="currentColor" />
          Restore
        </MenuItem>
      ) : (
        <MenuItem type="button" $danger onClick={() => run({ type: 'TRASH_TASK', taskId: task.id })}>
          <MaterialIcon name="delete_outline" size={16} color="currentColor" />
          Trash
        </MenuItem>
      )}
    </MenuList>
  );

  const choices =
    view === 'move' ? (
      <>
        <MenuHeading>Move it to which category?</MenuHeading>
        <ChoiceList>
          {state.categories
            .filter(item => item.id !== task.categoryId)
            .map(item => (
              <ConvertChoice
                key={item.id}
                type="button"
                onClick={() => run({ type: 'REASSIGN_CATEGORY', taskId: task.id, categoryId: item.id })}
              >
                {item.emoji} {item.name}
              </ConvertChoice>
            ))}
          {task.categoryId != null && (
            <ConvertChoice
              type="button"
              onClick={() => run({ type: 'REASSIGN_CATEGORY', taskId: task.id, categoryId: null })}
            >
              No category
            </ConvertChoice>
          )}
        </ChoiceList>
      </>
    ) : isReading ? (
      <>
        <MenuHeading>Which category does it belong to?</MenuHeading>
        <ChoiceList>
          {state.categories.map(item => (
            <ConvertChoice
              key={item.id}
              type="button"
              onClick={() => run({ type: 'CONVERT_TO_REGULAR', taskId: task.id, categoryId: item.id })}
            >
              {item.emoji} {item.name}
            </ConvertChoice>
          ))}
          <ConvertChoice
            type="button"
            onClick={() => run({ type: 'CONVERT_TO_REGULAR', taskId: task.id, categoryId: null })}
          >
            No category
          </ConvertChoice>
        </ChoiceList>
      </>
    ) : (
      <>
        <MenuHeading>Which book is it a reading from?</MenuHeading>
        {state.sources.length > 0 ? (
          <ChoiceList>
            {state.sources.map(source => (
              <ConvertChoice
                key={source.id}
                type="button"
                onClick={() => run({ type: 'CONVERT_TO_READING', taskId: task.id, sourceId: source.id })}
              >
                {source.name}
              </ConvertChoice>
            ))}
          </ChoiceList>
        ) : (
          <PopupHint>This course has no books to read from.</PopupHint>
        )}
      </>
    );

  return (
    <TippyTooltip
      target={
        <MoreButton type="button" aria-label="More actions" $open={open} onClick={toggle}>
          <MaterialIcon name="more_horiz" size={18} color="currentColor" />
        </MoreButton>
      }
      content={
        view === 'menu' ? (
          menu
        ) : (
          <ChoicesPanel>
            {choices}
            <BackLink type="button" onClick={() => setView('menu')}>
              ‹ Back
            </BackLink>
          </ChoicesPanel>
        )
      }
      position="bottom-end"
      interactive
      controlled
      visible={open}
      onClickOutside={close}
      lightTheme
      noPadding
      arrow={false}
      width="none"
      appendTo={estimatePopupAppendTo}
      zIndex={200}
      onCreate={(instance: TippyHandle) => {
        tippy.current = instance;
      }}
    />
  );
};

export const RegularTaskRow = ({
  task,
  category,
  model
}: {
  task: EstimatorTask;
  category?: EstimatorCategory;
  model: TaskEstimatorModel;
}) => {
  const { state, dispatch, selectTask } = model;
  const selected = state.ui.selectedIds.includes(task.id);
  const chip = regularChip(task);
  const isPreset = task.minutes != null && TASK_ESTIMATE_OPTIONS.includes(task.minutes);

  return (
    <TaskRow $flash={state.ui.flashingIds.includes(task.id)} $open={state.ui.openTaskId === task.id} data-row={task.id}>
      <TaskLeft>
        <CheckBox checked={selected} onChange={(_, e) => selectTask(task.id, !selected, e?.shiftKey)} />
        <TaskOpenArea task={task} />
      </TaskLeft>
      <TaskRightGroup>
        <TaskEstimatePopup
          task={task}
          model={model}
          trigger={toggle => (
            <EstimatorChip
              tone={chip.tone}
              label={chip.label}
              onClick={toggle}
              onClear={
                chip.clear === 'reset'
                  ? () => dispatch({ type: 'CLEAR_TASK', taskId: task.id })
                  : chip.clear === 'trash'
                  ? () => dispatch({ type: 'UNTRASH_TASK', taskId: task.id })
                  : undefined
              }
            />
          )}
        >
          {act => (
            <>
              <EstimatePopupLabel>⏱️ How long will it take?</EstimatePopupLabel>
              <PillGroup>
                {TASK_ESTIMATE_OPTIONS.map(minutes => (
                  <Pill
                    key={minutes}
                    type="button"
                    $selected={task.minutes === minutes}
                    $positive
                    onClick={() => act({ type: 'SET_TASK_MINUTES', taskId: task.id, minutes })}
                  >
                    {formatPlanningMinutes(minutes)}
                  </Pill>
                ))}
              </PillGroup>
              <PopupField>
                <CustomDurationPop
                  value={isPreset ? null : task.minutes}
                  onApply={minutes => act({ type: 'SET_TASK_MINUTES', taskId: task.id, minutes })}
                />
              </PopupField>
            </>
          )}
        </TaskEstimatePopup>
        <TaskActionsMenu task={task} model={model} />
      </TaskRightGroup>
    </TaskRow>
  );
};

export const ReadingTaskRow = ({ task, model }: { task: EstimatorTask; model: TaskEstimatorModel }) => {
  const { state, dispatch, selectTask, readingWasOverridden } = model;
  const selected = state.ui.selectedIds.includes(task.id);
  const wasOverridden = readingWasOverridden(task);
  const chip = readingChip(task, wasOverridden);

  return (
    <TaskRow $flash={state.ui.flashingIds.includes(task.id)} $open={state.ui.openTaskId === task.id} data-row={task.id}>
      <TaskLeft>
        <CheckBox checked={selected} onChange={(_, e) => selectTask(task.id, !selected, e?.shiftKey)} />
        <TaskOpenArea task={task}>
          {task.detectedPages != null && !task.trashed && (
            <>
              <DetectedMeta $struck={wasOverridden}>{task.detectedRangeLabel}</DetectedMeta>
              {wasOverridden && <EditedMeta>edited</EditedMeta>}
            </>
          )}
        </TaskOpenArea>
      </TaskLeft>
      <TaskRightGroup>
        <TaskEstimatePopup
          task={task}
          model={model}
          trigger={toggle => (
            <EstimatorChip
              tone={chip.tone}
              label={chip.label}
              onClick={toggle}
              onClear={
                chip.clear === 'detected'
                  ? () => dispatch({ type: 'RESTORE_DETECTED', taskId: task.id })
                  : chip.clear === 'reset'
                  ? () => dispatch({ type: 'CLEAR_PAGES', taskId: task.id })
                  : chip.clear === 'trash'
                  ? () => dispatch({ type: 'UNTRASH_TASK', taskId: task.id })
                  : undefined
              }
            />
          )}
        >
          {act => (
            <>
              <EstimatePopupLabel>📖 How many pages is it?</EstimatePopupLabel>
              <CustomPagesPop
                value={task.rawPagesInput || ''}
                bare
                onApply={raw => {
                  const parsed = parsePagesInput(raw);
                  if (!parsed) return false;
                  act({
                    type: 'SET_PAGES',
                    taskId: task.id,
                    pages: parsed.pages,
                    from: parsed.from ?? null,
                    to: parsed.to ?? null,
                    rangeLabel: parsed.rangeLabel,
                    rawPagesInput: raw.trim()
                  });
                  return true;
                }}
              />
              {wasOverridden && (
                <PopupHint>
                  Found in the syllabus: {task.detectedRangeLabel}.{' '}
                  <BackLink type="button" onClick={() => act({ type: 'RESTORE_DETECTED', taskId: task.id })}>
                    Use that
                  </BackLink>
                </PopupHint>
              )}
            </>
          )}
        </TaskEstimatePopup>
        <TaskActionsMenu task={task} model={model} />
      </TaskRightGroup>
    </TaskRow>
  );
};

// The time field and its Apply sit at the right, under the presets' end and where the popup opens from.
const PopupField = styled.div`
  > div {
    justify-content: flex-end;
  }
`;

const ChipSlot = styled.span`
  display: inline-flex;
`;

const PopupTaskName = styled(MiniText)`
  display: block;
  max-width: 340px;
  margin-bottom: -4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11.5px;
  color: ${props => props.theme.disabledTextColor};
`;

const PopupHint = styled(MiniText)`
  display: block;
  font-size: 12px;
  color: ${props => props.theme.textColor};
`;

// Light until pointed at or open, so a column of them doesn't compete with the times beside them.
const MoreButton = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: ${props => (props.$open ? props.theme.backgroundDark : 'none')};
  color: ${props => (props.$open ? props.theme.textStrongColor : props.theme.disabledTextColor)};

  &:hover {
    background: ${props => props.theme.backgroundDark};
    color: ${props => props.theme.textStrongColor};
  }
`;

// The app's dropdown look (MoreOptionDropdown): a white box of icon-and-label rows.
const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  min-width: 210px;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  font-family: 'Quicksand', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textColor};

  &:hover {
    background: ${props => props.theme.backgroundLight};
    color: ${props => (props.$danger ? colors.negative : props.theme.textStrongColor)};
  }
`;

const ChoicesPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
`;

const MenuHeading = styled(MiniText)`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textStrongColor};
`;

const ChoiceList = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 360px;
`;

const BackLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: 'Quicksand', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.primary};

  &:hover {
    text-decoration: underline;
  }
`;

export const SourceRateRow = ({ source, model }: { source: EstimatorReadingSource; model: TaskEstimatorModel }) => {
  const { state, dispatch } = model;
  const customClicked = state.ui.openCustomKeys.includes(`rate-${source.id}`);
  const customOpen = !isRatePreset(source.secondsPerPage) || customClicked;
  const toast = state.ui.toasts[`rate-${source.id}`] || '';
  return (
    <QuestionRow>
      <DefaultQuestion>How long does one page take you?</DefaultQuestion>
      <RatePills
        secondsPerPage={source.secondsPerPage}
        onPreset={secondsPerPage => dispatch({ type: 'SET_RATE', sourceId: source.id, secondsPerPage })}
        onCustomToggle={() => dispatch({ type: 'TOGGLE_CUSTOM', key: `rate-${source.id}` })}
        trailing={toast && <DefaultToast>{toast}</DefaultToast>}
      />
      {customOpen && (
        <CustomDurationPop
          inSeconds
          autoFocus={customClicked}
          value={source.secondsPerPage}
          onApply={secondsPerPage => dispatch({ type: 'SET_RATE', sourceId: source.id, secondsPerPage })}
        />
      )}
    </QuestionRow>
  );
};

export const TaskRows = ({
  tasks,
  model,
  category
}: {
  tasks: EstimatorTask[];
  model: TaskEstimatorModel;
  category?: EstimatorCategory;
}) => (
  <TaskList>
    {tasks.map(task =>
      task.kind === 'reading' ? (
        <ReadingTaskRow key={task.id} task={task} model={model} />
      ) : (
        <RegularTaskRow key={task.id} task={task} category={category} model={model} />
      )
    )}
  </TaskList>
);
